/**
 * PackageScopeSection
 * Radio-based mode selector (CATEGORY | EQUIPMENT) with conditional content.
 * Handles mode-switch confirmation via LinkingModeSwitchDialog.
 */

import type { PackageLinkingMode } from '@/api/packages';
import { Card, CardContent } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { LinkingModeSwitchDialog } from './LinkingModeSwitchDialog';
import { RentalEquipmentPicker, type PickedEquipment } from './RentalEquipmentPicker';

const EQUIPMENT_CATEGORIES = [
  'POWER_TOOL',
  'GARDEN',
  'CONSTRUCTION',
  'CLEANING',
  'MACHINERY',
  'HAND_TOOL',
  'MEASUREMENT',
  'SAFETY',
] as const;

export type EquipmentCategoryValue = (typeof EQUIPMENT_CATEGORIES)[number];

const CATEGORY_I18N_KEYS: Record<EquipmentCategoryValue, string> = {
  POWER_TOOL: 'rental.category.electricMachines',
  GARDEN: 'rental.category.outdoorMachines',
  CONSTRUCTION: 'rental.category.construction',
  CLEANING: 'rental.category.cleaning',
  MACHINERY: 'rental.category.machinery',
  HAND_TOOL: 'rental.category.handTool',
  MEASUREMENT: 'rental.category.measurement',
  SAFETY: 'rental.category.safety',
};

interface Props {
  linkingMode: PackageLinkingMode;
  categories: EquipmentCategoryValue[];
  equipment: PickedEquipment[];
  onChange: (next: {
    linkingMode: PackageLinkingMode;
    categories: EquipmentCategoryValue[];
    equipment: PickedEquipment[];
  }) => void;
}

export function PackageScopeSection({ linkingMode, categories, equipment, onChange }: Props) {
  const { t } = useTranslation();
  const [pendingMode, setPendingMode] = useState<PackageLinkingMode | null>(null);

  const requestModeSwitch = (next: PackageLinkingMode) => {
    if (next === linkingMode) return;
    const otherSideHasItems =
      (next === 'EQUIPMENT' && categories.length > 0) ||
      (next === 'CATEGORY' && equipment.length > 0);
    if (otherSideHasItems) {
      setPendingMode(next);
    } else {
      onChange({ linkingMode: next, categories, equipment });
    }
  };

  const confirmSwitch = () => {
    if (!pendingMode) return;
    onChange({
      linkingMode: pendingMode,
      categories: pendingMode === 'CATEGORY' ? categories : [],
      equipment: pendingMode === 'EQUIPMENT' ? equipment : [],
    });
    setPendingMode(null);
  };

  const dialogItemCount = pendingMode === 'EQUIPMENT' ? categories.length : equipment.length;

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {t('packages.scope.sectionTitle')}
        </h2>

        {/* Mode radio buttons */}
        <div className="mb-4 flex gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={linkingMode === 'CATEGORY'}
              onChange={() => requestModeSwitch('CATEGORY')}
              className="h-4 w-4"
            />
            <span className="text-foreground">{t('packages.scope.mode.category')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={linkingMode === 'EQUIPMENT'}
              onChange={() => requestModeSwitch('EQUIPMENT')}
              className="h-4 w-4"
            />
            <span className="text-foreground">{t('packages.scope.mode.equipment')}</span>
          </label>
        </div>

        {/* CATEGORY mode: checkbox grid */}
        {linkingMode === 'CATEGORY' && (
          <div className="flex flex-wrap gap-4">
            {EQUIPMENT_CATEGORIES.map(cat => (
              <label key={cat} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={categories.includes(cat)}
                  onChange={e => {
                    onChange({
                      linkingMode,
                      equipment,
                      categories: e.target.checked
                        ? [...categories, cat]
                        : categories.filter(c => c !== cat),
                    });
                  }}
                  className="h-4 w-4"
                />
                <span className="text-foreground">{t(CATEGORY_I18N_KEYS[cat])}</span>
              </label>
            ))}
          </div>
        )}

        {/* EQUIPMENT mode: two-panel picker */}
        {linkingMode === 'EQUIPMENT' && (
          <RentalEquipmentPicker
            selected={equipment}
            onChange={next => onChange({ linkingMode, categories, equipment: next })}
          />
        )}
      </CardContent>

      <LinkingModeSwitchDialog
        open={pendingMode !== null}
        itemCount={dialogItemCount}
        onConfirm={confirmSwitch}
        onCancel={() => setPendingMode(null)}
      />
    </Card>
  );
}
